import { siteSlugSchema, suggestSlug, type PublicationDto, type PublishStep } from '@ads/shared';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import * as Ic from '../components/icons';
import { ApiError } from '../lib/api';
import { usePublication, usePublications, usePublish, usePublishSettings, useSession, useSites } from '../lib/queries';

/* ─────────────────────────────────────────────────────────────────────
   Aprovar e publicar. Um painel lateral: o endereço, os destinos, e
   depois os passos acontecendo — pacote, commit, servidor, deploy,
   conferência — até o link no ar.
   ───────────────────────────────────────────────────────────────────── */

/** "Landing page — Converter visitas em matrículas" → "matrículas": a última palavra que diz algo. */
const keyword = (title: string) => {
  const words = title.split(/[—–]/).slice(1).join(' ').split(/\s+/).filter(w => /^\p{L}{4,}$/u.test(w));
  return words[words.length - 1] ?? '';
};

export function PublishSheet({ artifactId, title, tool, onClose }: { artifactId: string; title: string; tool: string; onClose: () => void }) {
  const { me } = useSession();
  const settings = usePublishSettings();
  const pubs = usePublications(artifactId);
  const sites = useSites();
  const publish = usePublish(artifactId);
  const [pubId, setPubId] = useState<string | null>(null);
  const pub = usePublication(pubId);

  const last = pubs.data?.[0];
  const fallback = suggestSlug(me.org.name, tool === 'landing' ? keyword(title) : '');
  const [slug, setSlug] = useState<string | null>(null);
  const value = slug ?? last?.slug ?? fallback;
  const parsed = siteSlugSchema.safeParse(value);
  const existing = sites.data?.find(s => s.slug === value);

  const s = settings.data;
  const [useGh, setUseGh] = useState(true);
  const [useEp, setUseEp] = useState(true);
  const gh = s?.github ?? null;
  const ep = s?.easypanel ?? null;
  const ghOn = !!gh && gh.ready && useGh;
  const epOn = !!ep && ep.ready && useEp;
  const adsHost = !epOn && !!s?.server.adsHosting;
  const url = epOn ? `https://${ep!.domainTemplate.replace(/\{slug\}/g, value)}/`
    : adsHost ? `${s!.server.publicUrl ?? window.location.origin}/s/${value}/` : null;
  const nothing = !ghOn && !epOn && !adsHost;

  const running = pub.data?.status === 'running' || publish.isPending;
  const phase: 'form' | 'run' | 'done' | 'failed' = !pubId ? 'form' : pub.data?.status === 'done' ? 'done' : pub.data?.status === 'failed' ? 'failed' : 'run';

  const close = () => { if (!running) onClose(); };
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  });
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { input.current?.focus({ preventScroll: true }); }, [settings.isSuccess]);

  const go = () => {
    if (!parsed.success || nothing) return;
    publish.mutate({ slug: value, github: ghOn, easypanel: epOn }, { onSuccess: p => setPubId(p.id) });
  };

  const err = publish.error instanceof ApiError ? publish.error.message : publish.error ? 'Não foi possível publicar.' : null;

  /* fora da árvore da bancada: ela tem transform e prenderia o position:fixed */
  return createPortal(
    <div className="pub-scrim" onMouseDown={e => { if (e.target === e.currentTarget) close(); }}>
      <aside className="pub" role="dialog" aria-modal="true" aria-labelledby="pubTitle" data-phase={phase}>
        <header className="pub-h">
          <div>
            <span className="pub-kicker"><Ic.Rocket />Publicação</span>
            <h2 id="pubTitle">{phase === 'done' ? (pub.data?.url ? 'No ar.' : 'Versionado.') : phase === 'failed' ? 'A publicação parou.' : phase === 'run' ? 'Publicando…' : 'Aprovar e publicar'}</h2>
          </div>
          <button className="pub-x" type="button" onClick={close} disabled={running} aria-label="Fechar"><Ic.Close /></button>
        </header>

        <div className="pub-body">
          {phase === 'form' ? (
            <>
              <p className="pub-lede">O site vai ao ar exatamente como está no canvas — mesmo HTML, mesmo design system. Cada publicação fica guardada e pode ser refeita.</p>

              <div className="pub-field">
                <label htmlFor="pubSlug">Endereço</label>
                <div className={`pub-slug${parsed.success ? '' : ' bad'}`}>
                  <input id="pubSlug" ref={input} value={value} maxLength={40} spellCheck={false} autoComplete="off"
                    onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                    onKeyDown={e => { if (e.key === 'Enter') go(); }} />
                </div>
                {!parsed.success
                  ? <span className="f-hint bad">{parsed.error.issues[0]?.message}</span>
                  : url ? <span className="pub-url-pre"><Ic.Globe />{url.replace(/^https?:\/\//, '')}</span> : null}
                {existing && parsed.success && <span className="f-hint">Já publicado neste endereço — a versão no ar será substituída (a anterior continua no histórico).</span>}
              </div>

              <div className="pub-targets">
                <Target on={ghOn} available={!!gh?.ready} onToggle={() => setUseGh(v => !v)} icon={<Ic.Branch />}
                  title="Versionar no GitHub"
                  desc={gh ? `${gh.repo} · ${[gh.basePath, value].filter(Boolean).join('/')}/` : 'Cada publicação vira um commit no repositório do cliente.'}
                  problem={gh && !gh.ready ? gh.problem : !gh ? 'Não configurado' : null} />
                {ep ? (
                  <Target on={epOn} available={ep.ready} onToggle={() => setUseEp(v => !v)} icon={<Ic.Cube />}
                    title="Publicar no EasyPanel"
                    desc={`Projeto ${ep.project} · ${ep.mode === 'github' ? 'constrói do GitHub' : 'pacote do ADS'} · HTTPS automático`}
                    problem={!ep.ready ? ep.problem : null} />
                ) : (
                  <Target on={false} available={false} onToggle={() => {}} icon={<Ic.Cube />} title="Publicar no EasyPanel"
                    desc="O site vira um serviço com domínio e HTTPS." problem="Não configurado neste servidor" />
                )}
                {adsHost && (
                  <div className="pub-note"><Ic.Eye /><p><b>Hospedagem de prévia do ADS.</b> Sem EasyPanel, o próprio ADS serve o site neste endereço — ótimo para aprovar com o cliente.</p></div>
                )}
              </div>

              {(!gh || !ep) && me.org.role === 'owner' && (
                <p className="pub-cfg"><Link to="/configuracoes?aba=publicacao" onClick={onClose}>Configurar GitHub e EasyPanel<Ic.ChevronRight /></Link></p>
              )}
              {err && <div className="notice warn"><Ic.Warning /><p>{err}</p></div>}
            </>
          ) : (
            <Progress p={pub.data ?? null} />
          )}
        </div>

        <footer className="pub-f">
          {phase === 'form' && <>
            <button className="btn-ghost" type="button" onClick={close}>Cancelar</button>
            <button className="btn-primary" type="button" disabled={!parsed.success || nothing || publish.isPending || !s} onClick={go}>
              {publish.isPending ? <><span className="spin" />Enviando…</> : <><Ic.Rocket />Aprovar e publicar</>}
            </button>
          </>}
          {phase === 'run' && <button className="btn-primary is-busy" type="button" disabled><span className="spin" />Publicando…</button>}
          {phase === 'done' && <>
            <button className="btn-ghost" type="button" onClick={onClose}>Fechar</button>
            {pub.data?.url && <a className="btn-primary" href={pub.data.url} target="_blank" rel="noopener">Abrir o site<Ic.External /></a>}
          </>}
          {phase === 'failed' && <>
            <button className="btn-ghost" type="button" onClick={onClose}>Fechar</button>
            <button className="btn-primary" type="button" onClick={() => { setPubId(null); publish.reset(); }}>Tentar de novo<Ic.Refresh /></button>
          </>}
        </footer>
      </aside>
    </div>,
    document.body,
  );
}

function Target({ on, available, onToggle, icon, title, desc, problem }: {
  on: boolean; available: boolean; onToggle: () => void; icon: ReactNode; title: string; desc: string; problem: string | null;
}) {
  return (
    <button type="button" className="pub-target" role="switch" aria-checked={on} disabled={!available} onClick={onToggle}>
      <span className="pt-ic">{icon}</span>
      <span className="pt-t"><b>{title}</b><span>{problem ?? desc}</span></span>
      <span className="pt-sw" aria-hidden="true"><i /></span>
    </button>
  );
}

const STEP_IC: Record<PublishStep['status'], ReactNode> = {
  todo: null, running: <span className="spin" />, done: <Ic.Check />, skipped: <i className="dash" />, warn: <b>!</b>, failed: <Ic.Close />,
};

function Progress({ p }: { p: PublicationDto | null }) {
  const [copied, setCopied] = useState(false);
  if (!p) return <div className="pub-steps"><div className="pub-step" data-s="running"><span className="ps-dot"><span className="spin" /></span><span className="ps-t"><b>Iniciando</b></span></div></div>;
  const done = p.status === 'done';
  return (
    <>
      {done && p.url && (
        <div className="pub-live">
          <span className="pub-seal"><Ic.Check /></span>
          <a className="pub-link" href={p.url} target="_blank" rel="noopener">{p.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}<Ic.External /></a>
          <div className="pub-live-act">
            <button type="button" onClick={() => { void navigator.clipboard?.writeText(p.url!); setCopied(true); setTimeout(() => setCopied(false), 1600); }}>
              {copied ? <><Ic.Check />Copiado</> : <><Ic.Copy />Copiar link</>}
            </button>
            {p.commitUrl && <a href={p.commitUrl} target="_blank" rel="noopener"><Ic.Branch />Ver commit {p.commitSha?.slice(0, 7)}</a>}
          </div>
        </div>
      )}
      {done && !p.url && p.commitUrl && (
        <div className="pub-live">
          <span className="pub-seal"><Ic.Branch /></span>
          <a className="pub-link" href={p.commitUrl} target="_blank" rel="noopener">Commit {p.commitSha?.slice(0, 7)} no GitHub<Ic.External /></a>
        </div>
      )}
      <ol className="pub-steps">
        {p.steps.map(s => (
          <li key={s.key} className="pub-step" data-s={s.status}>
            <span className="ps-dot">{STEP_IC[s.status]}</span>
            <span className="ps-t">
              <b>{s.label}</b>
              {s.detail && <span>{s.detail}</span>}
              {s.href && s.status !== 'running' && s.key !== 'verify' && (
                <a href={s.href} target="_blank" rel="noopener">{s.key === 'github' ? 'Abrir commit' : s.key === 'host' ? 'Abrir no painel' : 'Abrir'}<Ic.External /></a>
              )}
            </span>
          </li>
        ))}
      </ol>
      {p.status === 'failed' && p.error && <div className="notice warn"><Ic.Warning /><p>{p.error}</p></div>}
      <p className="pub-meta">Publicação {p.bundleId}{p.fileCount ? ` · ${p.fileCount} arquivos` : ''}{p.createdBy ? ` · aprovada por ${p.createdBy}` : ''}</p>
    </>
  );
}
